import { Modal, UI, Utils } from 'common';
import React, { useContext, useEffect, useState } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { CancelAccountContext } from './CancelAccount.context';

const { Session, Util, Logger } = Utils;
const { WrappedButton, WrappedInputText } = UI;
const { ModalContext, ModalButtonActionType } = Modal;

const ClickToCall = () => {
	const cancelAccountContext = useContext(CancelAccountContext);
	const { customerDetails = {} } = Session.getAccountInfo();
	const { address = {} } = customerDetails;
	const { phone = '', name = '' } = address;

	const [phoneNumber, setPhoneNumber] = useState(
		cancelAccountContext.ClickToCallPhoneNumber
	);
	const { showError } = useContext(ModalContext);
	const [firstName = '', lastName = ''] = name.split(' ');

	Logger.logToServer({
		category: 'cancel-c2c',
		action: 'c2c-shown',
	});

	// WEBAPPS-31155, phone number here is specific for click to call feature, which is different from the one in Session.
	const brandPhone = '1-877-695-3859';

	useEffect(() => {
		// get the address phone number and use it as the default phone number
		if (customerDetails.hasOwnProperty('address')) {
			setPhoneNumber(Util.formatPhoneNumber(phone.substring(0, 10))); // exclude extension
		} else {
			setPhoneNumber('');
		}
	}, []);

	const showInvalidPhoneNumberError = () => {
		showError({
			data: {
				title: 'Invalid Phone Number',
				description: `
					<p>Please provide a valid phone number.</p>
				`,
				primaryButtonType: ModalButtonActionType.Cancel,
				primaryButtonText: 'Close',
				handleType: 'custom',
			},
		});
	};

	// this is called from the modal dialog (search closeCallback) when the Continue To Cancel button is clicked
	const continueCancelAccount = closeType => {
		if (closeType === 'OK')
			cancelAccountContext.setStep('CancellationQuestions');
	};

	const onCallMeNowButtonClick = async () => {
		// only allows 0-9 inputs
		const actualNumber = phoneNumber.replace(/[^\d]/g, '');

		if (actualNumber.length < 10) {
			showInvalidPhoneNumberError();
			return;
		}

		Logger.logToServer({
			category: 'cancel-c2c',
			action: 'call-button-clicked',
		});

		const response = await AccountCommunication.setClickToCall({
			PhoneNumber: actualNumber,
			FirstName: firstName,
			LastName: lastName,
		});

		if (AccountCommunication.isSuccessful(response)) {
			cancelAccountContext.setClickToCallValues(phoneNumber);
			cancelAccountContext.setStep('ClickToCallConfirmation');
		} else if (response.ErrorCode === 1000006) {
			showInvalidPhoneNumberError();
		} else {
			showError({
				data: {
					title: 'Unexpected Error',
					description: `
							<p>We encountered an unexpected error when attempting to call the number you provided.</p>
							<p>Please give us a call at ${brandPhone} or continue to cancel online.</p>
							`,
					primaryButtonType: ModalButtonActionType.Cancel,
					primaryButtonText: 'Close',
					secondaryButtonType: ModalButtonActionType.Ok,
					secondaryButtonText: 'Continue to Cancel',
					handleType: 'custom',
				},
				closeCallback: continueCancelAccount,
			});
		}
	};

	const onInputTextChange = e => {
		const { value } = e.target;
		setPhoneNumber(Util.formatPhoneNumber(value));
	};

	// this is called from clicking the Continue to cancel link
	const onClickContinueCancelAccount = e => {
		e.stopPropagation();

		Logger.logToServer({
			category: 'cancel-c2c',
			action: 'call-button-skipped',
		});

		cancelAccountContext.setStep('CancellationQuestions');
	};

	return (
		<div className="cancel-account-click-to-call">
			<h1 className="primary-header">We miss you already.</h1>

			<p>
				We're not sure exactly why you're considering leaving Stamps.com, but
				would love to learn more. Your feedback is extremely important to us.
				Please allow one of our team members to reach out to you and take you
				through our quick and easy cancellation process by phone. There may be a
				special offer in it for you. Or give us a call at {brandPhone}.
			</p>
			<div className="click-to-call-data">
				<div className="left">
					<div className="call-me-now-row">
						<WrappedInputText
							name="clickToCallPhoneNumber"
							value={phoneNumber}
							maxLength="14" // (562) 555-1212
							dataqa="clickToCallPhoneNumber"
							placeholder="Phone Number"
							onChange={onInputTextChange}
						/>
						<WrappedButton
							className="call-me-now-button"
							label="CALL ME NOW"
							onClick={onCallMeNowButtonClick}
							dataqa="click-to-call-call-now"
						/>
					</div>
					<p className="fine-print">
						By choosing Call me now, I give my consent to receive calls from or
						on behalf of Stamps.com to contact me at the number provided. I
						understand that I can revoke this consent at any time and consent is
						not a condition to purchase.
					</p>
				</div>

				<div className="right">
					<a
						href={null}
						onClick={onClickContinueCancelAccount}
						role="button"
						tabIndex="0"
					>
						Continue to cancel
					</a>
				</div>
			</div>
		</div>
	);
};

export default ClickToCall;
